
import {
  Vue, Component, PropSync, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import PickupSpecificsModal from '@/features/schedule-pickup/components/PickupSpecificsModal.vue';
import PickupModal from '@/features/schedule-pickup/components/PickupModal.vue';
import {
  ScheduleDataType, SchedulePickupRequest, SchedulePickupResponse, SelectedUnscheduledPickup,
} from '@/features/schedule-pickup/types';
import { getSchedulePickupNotifications } from '@/features/notifications/helpers/schedulePickup';
import { ShipFrom } from '@/features/shipment/types';
import { scheduleAPickup } from '@/features/schedule-pickup/services/schedulePickup.services';
import { showErrorAlertDialog } from '@/features/common/helpers';

@Component({
  components: {
    PickupModal,
    PickupSpecificsModal,
  },
})
export default class SchedulePickupFlow extends Vue {
  @PropSync('show', { required: true }) showSchedulePickup!: boolean;

  @Prop({ required: true }) selectedPickups!: SelectedUnscheduledPickup[];

  private scheduleData: ScheduleDataType = {
    pickupDate: null,
    pickupLocation: '',
    comments: '',
    earliestTime: null,
    latestTime: '',
  }

  private showPickupSpecificsModal = false;

  private showPickupModal = false;

  private isLoading = false;

  private pickupResponse: SchedulePickupResponse | null = null;

  private oopsShow = false;

  private get fromAddress(): ShipFrom | null {
    if (!this.selectedPickups.length) return null;
    if (!this.selectedPickups[0]) return null;
    return this.selectedPickups[0].fromAddress;
  }

  @Watch('show')
  private openPickupSpecificsModal(isShow: boolean) {
    if (!isShow) return;

    if (!this.selectedPickups.length) {
      showErrorAlertDialog(
        this.$t('scheduledPickups.pleaseSelectAShipment') as string,
      );
      this.showSchedulePickup = false;
      return;
    }

    this.showPickupSpecificsModal = true;
  }

  @Watch('showPickupSpecificsModal')
  private hidePickupSpecificsModal(isShow: boolean) {
    if (isShow) return;
    this.showSchedulePickup = false;
  }

  private openPickupModal(): void {
    const onNotificationsSubmit = (): void => {
      this.showPickupModal = !this.showPickupModal;
    };

    getSchedulePickupNotifications(this.selectedPickups, onNotificationsSubmit);
  }

  private async proceedToSchedulePickup() {
    this.isLoading = true;

    const trackingNumbers = this.selectedPickups.map((item) => item.trackingNumber);

    const {
      pickupDate,
      pickupLocation,
      comments,
      latestTime,
    } = this.scheduleData;

    let { earliestTime } = this.scheduleData;

    if (earliestTime === 'It\'s Ready Now') {
      earliestTime = null;
      if (pickupDate?.day !== 'Today') {
        earliestTime = this.convertToISOTime(
          pickupDate?.value as string,
          '8:00 AM',
        );
      }
    } else {
      earliestTime = this.convertToISOTime(
        pickupDate?.value as string,
        earliestTime as string,
      );
    }

    const data: SchedulePickupRequest = {
      trackingNumbers,
      readyDateTime: earliestTime,
      closedDateTime: this.convertToISOTime(
        pickupDate?.value as string,
        latestTime,
      ),
      location: pickupLocation,
      comments,
    };

    this.pickupResponse = await scheduleAPickup(data, this.$route.fullPath);

    if (this.pickupResponse && this.pickupResponse.successful) {
      this.$router.push({
        name: 'pickup-schedule-confirm',
        query: { id: this.pickupResponse.id?.toString() },
      });
    } else {
      this.oopsShow = true;
    }

    this.showPickupModal = false;
    this.isLoading = false;
  }

  private convertToISOTime = (date: string, time: string): string => {
    return moment(`${date} ${time}`, 'YYYY-MM-DD h:mm A')
      .local()
      .format('YYYY-MM-DDTHH:mm:ss');
  }
}
